<script setup>
import { debounce, set } from "lodash-es";
import { HomeIcon, NewspaperIcon } from "lucide-vue-next";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useToast } from "vue-toastification";

import AppLayout from "@/Layouts/AppLayout.vue";

import ContentContainer from "@/Components/ContentContainer.vue";
import PullToRefresher from "@/Components/PullToRefreshContainer.vue";
import SlideContainer from "@/Components/SlideContainer.vue";

import { useCssProperty } from "@/composables/cssProperty";
import useSidebar from "@/composables/sidebar";
import { useScroll } from "@/composables/useScroll";
import { useTeamPermissions } from "@/composables/useTeamPermissions";
import { useTeamSubscription } from "@/composables/useTeamSubscription";
import { useResponsiveStore } from "@/stores/responsive";
import CampaignEditDrawer from "@campaign-js/Components/CampaignEditDrawer.vue";
import CampaignFooter from "@campaign-js/Components/CampaignFooter.vue";
import PageCommentList from "@campaign-js/Components/PageCommentList.vue";
import CampaignCoverPhoto from "@campaign-js/Layouts/Partials/CampaignCoverPhoto.vue";
import CampaignMenu from "@campaign-js/Layouts/Partials/CampaignMenu.vue";
import CampaignMobileHeader from "@campaign-js/Layouts/Partials/CampaignMobileHeader.vue";
import CampaignSidebar from "@campaign-js/Layouts/Partials/CampaignSidebar.vue";
import CampaignSubLink from "@campaign-js/Layouts/Partials/CampaignSubLink.vue";
import LayoutBackground from "@campaign-js/Layouts/Partials/LayoutBackground.vue";
import LogoMark from "@campaign-js/Layouts/Partials/LogoMark.vue";
import MatomoTracker from "@campaign-js/Layouts/Partials/MatomoTracker.vue";
import PageQuickMenu from "@campaign-js/Layouts/Partials/PageQuickMenu.vue";
import { router, usePage } from "@inertiajs/vue3";

defineProps({
  title: {
    type: String,
    required: false
  },
  description: {
    type: String,
    required: false
  },
  page: {
    type: Object,
    required: false
  }
});

const _page = usePage();
const toast = useToast();
const responsive = useResponsiveStore();
const { hasPermission } = useTeamPermissions();
const { isOnFreePlan } = useTeamSubscription();

const campaign = computed(() => _page.props.campaign);
const team = computed(() => _page.props.team);
const canDesign = computed(() => hasPermission("campaign:design:update"));
const isLoggedIn = computed(() => !!_page.props.user);
const hasFooterInfo = computed(() => campaign.value.settings.footers.length > 0 || campaign.value.settings.socials.length > 0);
const hasSubLink = computed(() => campaign.value.settings.links.length > 0);
const isBelongsCampaign = computed(() => team.value?.campaign.hashid === campaign.value?.hashid);
const isMember = computed(() => campaign.value.team.is_member);
const myTeam = computed(() => _page.props.team);
const isHomepageLayout = computed(() => campaign.value.layout_type === "homepage");

const debouncedUpdate = debounce(() => {
  axios.put(route("app.ajax.campaigns.update", [myTeam.value]), campaign.value).then(() => {
    toast.info("캠페인이 업데이트 되었습니다.", {
      hideProgressBar: true,
      position: "bottom-center",
      timeout: 2000
    });
  });
}, 1000);

const update = (key, value) => {
  set(campaign.value, key, value);
  debouncedUpdate();
};

const scroll = useScroll({ threshold: 50, topThreshold: 10 });
const sidebar = useSidebar();
const cssProperty = useCssProperty({
  "campaign-primary-color": campaign.value.settings.primary_color,
  "campaign-bg-color": campaign.value.settings.background_color,
  "campaign-color": campaign.value.settings.color
});

watch(
  () => campaign.value.settings.primary_color,
  () => cssProperty.setProperty("campaign-primary-color", campaign.value.settings.primary_color)
);

watch(
  () => campaign.value.settings.background_color,
  () => {
    cssProperty.setProperty("campaign-bg-color", campaign.value.settings.background_color);
    cssProperty.setProperty("campaign-color", campaign.value.settings.color);
  }
);

const navs = ref([
  {
    name: "홈",
    icon: HomeIcon,
    href: route("app.campaigns.index", campaign.value.team),
    current: route().current("app.campaigns.index")
  },
  {
    name: "포스트",
    icon: NewspaperIcon,
    href: route("app.campaigns.pages.index", campaign.value.team),
    current: route().current("app.campaigns.pages.*")
  }
]);

const profileActions = [
  {
    label: "내 캠페인",
    action: () => router.visit(route("teams.show", myTeam.value)),
    enable: true
  }
];

const customerActions = [
  {
    label: "로그인",
    action: () => router.visit(route("login")),
    enable: true
  },
  {
    label: "회원가입",
    action: () => router.visit(route("register")),
    enable: true
  }
];

const moveToPage = (direction) => {
  const currentIndex = navs.value.findIndex((menu) => menu.current);
  const nextIndex = direction === "left" ? currentIndex + 1 : currentIndex - 1;

  if (nextIndex >= 0 && nextIndex < navs.value.length) {
    router.visit(navs.value[nextIndex].href);
  }
};

const handleSlide = (direction) => {
  moveToPage(direction);
};

onMounted(() => {
  sidebar.disableAutoFold();
  sidebar.setPrimaryMinimized(true);

  document.body.classList.add("is-campaign");
});

onUnmounted(() => {
  sidebar.enableAutoFold();
  sidebar.setPrimaryMinimized(false);

  document.body.classList.remove("is-campaign");
});
</script>

<template>
  <AppLayout :title="title ?? campaign.name" blank main-class="!p-0 !ml-0" :main-style="{ color: campaign.settings.color }" :has-footer="false">
    <template #navbar>
      <div class="fixed inset-x-0 bottom-0 z-40 isp-pb-0" :class="[scroll.scrollTop > 50 ? 'bg-campaign' : 'bg-[#fff]']"></div>
      <!--end::navbar-->
    </template>

    <template #drawer>
      <CampaignEditDrawer v-if="isLoggedIn && canDesign" :campaign="campaign" :update="update" />
    </template>

    <LayoutBackground :campaign="campaign" />
    <!--end::background-->

    <ContentContainer classes="max-w-[900px] p-0 sm:p-0 isp-mt-0 xl:p-0 2xl:max-w-[900px] lg:pl-[var(--primary-sidebar-min-width)] lg:p-0">
      <div class="relative lg:flex xl:gap-x-8">
        <CampaignSidebar
          v-if="isHomepageLayout && responsive.lgAndUp"
          :campaign="campaign"
          :navs="navs"
          :is-member="isMember"
          :is-free-plan="isOnFreePlan"
          class="max-w-campaign"
        />
        <!--end::sidebar-->
        <!--end::for-desktop-->

        <PullToRefresher class="page campaign-content relative mx-auto min-h-screen w-full max-w-campaign" :class="{ full: !scroll.isAtTop }">
          <template #default="{ ptr }">
            <CampaignMobileHeader v-if="isHomepageLayout && responsive.mdAndDown" :campaign="campaign" :is-member="isMember" />
            <!--end::profile-->

            <CampaignCoverPhoto
              v-if="isHomepageLayout"
              :campaign="campaign"
              :pull-to-refresh="ptr"
              :is-logged-in="isLoggedIn"
              :profile-actions="profileActions"
              :customer-actions="customerActions"
            />
            <!--end::background-->

            <div
              :style="{
                transform: ptr.isDragging.value ? `translateY(${ptr.pullDistance.value}px)` : '',
                transition: ptr.isDragging.value ? 'none' : 'transform 200ms ease-out'
              }"
            >
              <CampaignMenu v-if="isHomepageLayout" :campaign="campaign" :is-member="isMember" :navs="navs" :has-sub-link="hasSubLink" />
              <!--end::menu-->

              <CampaignSubLink v-if="isHomepageLayout && hasSubLink" :campaign="campaign" />
              <!--end::sub-link-->

              <SlideContainer @slide="handleSlide">
                <div ref="campaignContainerRef" class="campaign-container">
                  <slot />

                  <div v-if="hasFooterInfo" class="mt-2 sm:hidden">
                    <CampaignFooter :footers="campaign.settings.footers" />
                  </div>
                  <!--end::footer-->
                </div>
              </SlideContainer>
              <!--end::content-->

              <slot name="footer" />

              <div v-if="isOnFreePlan" class="flex-center relative p-2 text-zinc-700 lg:hidden">
                <LogoMark />
              </div>
              <!--end::free-plan-mark-->
            </div>
            <!--end::translate-area-->

            <PageCommentList v-if="page" :campaign="campaign" :page="page" :is-logged-in="isLoggedIn" />
            <!--end::campaign-drawer's-->
          </template>
        </PullToRefresher>
        <!--end::for-all-->
      </div>
      <!--end::container-->

      <PageQuickMenu
        :campaign="campaign"
        :team="team"
        :page="page"
        :is-logged-in="isLoggedIn"
        :can-design="isBelongsCampaign && isLoggedIn && canDesign"
      />
      <!--end::quick-menu-->
    </ContentContainer>

    <MatomoTracker v-if="page" :page="page" />
    <!--end::matomo-->
  </AppLayout>
</template>
